import { useEffect, useState, type ReactNode } from 'react';
import { Check, Copy } from 'lucide-react';
import { Button } from '@/components/ui/primitives';

/**
 * A value shown to be copied: a password, a token, a command.
 *
 * Selectable as a whole, so a triple-click takes exactly the value; and a
 * button, because on a phone or a remote desktop a clipboard write is the one
 * path that does not drop a character.
 */
export function CopyField({
  label,
  value,
  multiline = false,
}: {
  readonly label: string;
  readonly value: string;
  /** Kept as written — line breaks and all — for commands meant to be pasted whole. */
  readonly multiline?: boolean;
}): ReactNode {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // Clipboard access can be refused; the value is on screen and selectable.
    }
  };

  return (
    <div className="stack stack-tight">
      <span className="field-label">{label}</span>
      <div className={`secret-line${multiline ? ' secret-line-block' : ''}`}>
        {multiline ? <pre>{value}</pre> : <code>{value}</code>}
        <Button size="sm" variant="ghost" icon={copied ? Check : Copy} onClick={() => void copy()} aria-label={`Скопировать: ${label}`}>
          {copied ? 'Скопировано' : 'Копировать'}
        </Button>
      </div>
    </div>
  );
}
